import { getToken, MessagePayload, onMessage } from 'firebase/messaging';

import { registerDevice, removeDeviceByToken } from '@/modules/notifications/api/notifications.api';
import { firebaseConfig, messaging } from '@/modules/notifications/firebase-config';
import { FcmNotificationType } from '@/modules/notifications/types/fcm-notification.type';
import {
  BaseFirebaseMessageData,
  FirebaseMessagePayload,
  NotificationHandler,
} from '@/modules/notifications/types/firebase-message';
import { NotificationPayload } from '@/modules/notifications/types/notification-payload';
import { NotificationPermissionState } from '@/modules/notifications/types/notification-permission-state';
import { getBrowserTimezone } from '@/utils/date-formatters/date-formatters';
import { logger } from '@/utils/logger/logger';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;

/**
 * Registers the Firebase messaging service worker with config passed via query params.
 * Waits for the service worker to be active before returning.
 */
const registerServiceWorker = async (): Promise<ServiceWorkerRegistration> => {
  const swUrl = new URL('/firebase-messaging-sw.js', window.location.origin);

  // Pass Firebase config as query params to the service worker
  swUrl.searchParams.set('apiKey', firebaseConfig.apiKey);
  swUrl.searchParams.set('authDomain', firebaseConfig.authDomain);
  swUrl.searchParams.set('projectId', firebaseConfig.projectId);
  swUrl.searchParams.set('storageBucket', firebaseConfig.storageBucket);
  swUrl.searchParams.set('messagingSenderId', firebaseConfig.messagingSenderId);
  swUrl.searchParams.set('appId', firebaseConfig.appId);
  if (firebaseConfig.measurementId) {
    swUrl.searchParams.set('measurementId', firebaseConfig.measurementId);
  }

  const registration = await navigator.serviceWorker.register(swUrl.toString());

  // If there's already an active service worker, use it immediately.
  // A new worker may be in 'waiting' state, but the active one is sufficient for FCM.
  if (registration.active) {
    return registration;
  }

  // Wait for a freshly installing service worker to become active
  const pendingWorker = registration.installing ?? registration.waiting;
  if (pendingWorker) {
    await new Promise<void>(resolve => {
      pendingWorker.addEventListener('statechange', function handler() {
        if (this.state === 'activated') {
          this.removeEventListener('statechange', handler);
          resolve();
        }
      });
    });
  }

  return registration;
};

/**
 * Browser's native notification permission values.
 * Mirrors the `NotificationPermission` type from the Web Notifications API.
 */
enum BrowserNotificationPermission {
  GRANTED = 'granted',
  DENIED = 'denied',
  DEFAULT = 'default',
}

const TAG = 'FirebaseNotificationService: ';

/** Interval for periodic token refresh checks (1 hour) */
const TOKEN_REFRESH_INTERVAL_MS = 60 * 60 * 1000;

/** localStorage key for persisting the FCM device token (used by debug tooling) */
export const FCM_DEVICE_TOKEN_STORAGE_KEY = 'fcm_device_token';

/**
 * Firebase Cloud Messaging (FCM) notification service
 *
 * Singleton service that manages Firebase push notifications including:
 * - Token generation and management
 * - Foreground message handling
 * - Type-based notification routing to subscribers
 *
 * @example Basic initialization (typically in AuthenticatedTemplate)
 * ```typescript
 * await notificationService.initialize();
 * ```
 *
 * @example Subscribe to specific notification types
 * ```typescript
 * const unsubscribe = notificationService.subscribe(FCMNotificationType.TEST, (notification) => {
 *   console.log('Test notification:', notification.title);
 *   toast.info(notification.body);
 * });
 *
 * // Clean up when component unmounts
 * return () => unsubscribe();
 * ```
 *
 * @example Subscribe to all notification types
 * ```typescript
 * const unsubscribe = notificationService.subscribe(FCMNotificationType.ALL, (notification) => {
 *   console.log('Received:', notification.type);
 * });
 * ```
 */

class FirebaseNotificationService {
  /** FCM device token for this browser/device. Null until initialized. */
  private token: string | null = null;

  /** Map of notification type to array of subscriber handler functions */
  private listeners: Map<FcmNotificationType, NotificationHandler[]> = new Map();

  /** Tracks whether service has been initialized to prevent duplicate initialization */
  private initialized: boolean = false;

  /** Cached service worker registration for token refresh */
  private serviceWorkerRegistration: ServiceWorkerRegistration | null = null;

  /** Interval ID for periodic token refresh */
  private tokenRefreshIntervalId: ReturnType<typeof setInterval> | null = null;

  /** Sets the token in memory and persists to localStorage for debug tooling */
  private setToken(value: string | null): void {
    this.token = value;
    try {
      if (value) {
        // JSON.stringify to match useLocalStorage hook's JSON serialization format
        localStorage.setItem(FCM_DEVICE_TOKEN_STORAGE_KEY, JSON.stringify(value));
      } else {
        localStorage.removeItem(FCM_DEVICE_TOKEN_STORAGE_KEY);
      }
    } catch {
      // localStorage may be unavailable in some contexts
    }
  }

  /**
   * Initialize Firebase Cloud Messaging service
   *
   * Performs the following steps:
   * 1. Requests browser notification permission from user
   * 2. Generates FCM device token
   * 3. Registers token with backend API
   * 4. Sets up foreground message listener
   *
   * This method is idempotent - calling it multiple times is safe.
   * Subsequent calls will return early if already initialized.
   *
   * @throws {Error} If FCM initialization fails or backend registration fails
   *
   */
  /**
   * Check whether the browser supports notifications and what the current permission state is.
   *
   * @returns `NotificationPermissionState` indicating the action to take
   */
  checkPermissionState(): NotificationPermissionState {
    if (this.initialized) return NotificationPermissionState.UNAVAILABLE;

    if (!VAPID_KEY || !messaging) return NotificationPermissionState.UNAVAILABLE;
    if (!('Notification' in window) || !('serviceWorker' in navigator)) {
      return NotificationPermissionState.UNAVAILABLE;
    }

    const permission = Notification.permission as BrowserNotificationPermission;

    if (permission === BrowserNotificationPermission.GRANTED) {
      return NotificationPermissionState.PROCEED;
    }
    if (permission === BrowserNotificationPermission.DENIED) {
      return NotificationPermissionState.UNAVAILABLE;
    }

    return NotificationPermissionState.PROMPT;
  }

  /**
   * Request browser notification permission and complete FCM initialization.
   *
   * Call this either:
   * - Directly when `checkPermissionState()` returns `PROCEED` (already granted)
   * - After the user accepts the pre-permission modal when state is `PROMPT`
   */
  async requestPermissionAndInitialize(): Promise<void> {
    if (this.initialized) {
      logger.debug(TAG, 'Already initialized');
      return;
    }

    if (!VAPID_KEY) {
      logger.error(TAG, 'VAPID key is not configured. Check VITE_FIREBASE_VAPID_KEY env variable.');
      return;
    }

    if (!messaging) {
      logger.error(TAG, 'Firebase messaging is not initialized');
      return;
    }

    if (!('Notification' in window)) {
      logger.warn(TAG, 'Browser does not support notifications');
      return;
    }

    if (!('serviceWorker' in navigator)) {
      logger.warn(TAG, 'Service workers are not supported');
      return;
    }

    try {
      const permission = await Notification.requestPermission();

      if (permission !== BrowserNotificationPermission.GRANTED) {
        logger.warn(TAG, 'Notification permission denied by user');
        return;
      }

      try {
        this.serviceWorkerRegistration = await registerServiceWorker();
      } catch (swError) {
        logger.error(TAG, 'Failed to register service worker:', swError);
        return;
      }

      const token = await this.fetchToken();
      if (!token) return;

      this.setToken(token);

      try {
        logger.debug(TAG, `Firebase device token: ${token}`);
        await registerDevice({ token, timezone: getBrowserTimezone() });
      } catch (registerError) {
        logger.error(TAG, 'Failed to register device on backend:', registerError);
      }

      onMessage(messaging, (payload: MessagePayload) => {
        this.handleMessage(payload as FirebaseMessagePayload);
      });

      navigator.serviceWorker.addEventListener('message', event => {
        if (event.data?.type === 'FCM_BACKGROUND_MESSAGE') {
          this.handleMessage(event.data.payload as FirebaseMessagePayload);
        }
      });

      this.startTokenRefresh();
      this.initialized = true;
      logger.info(TAG, 'Initialized successfully');
    } catch (error) {
      logger.error(TAG, 'Unexpected initialization error:', error);
      throw error;
    }
  }

  async initialize(): Promise<void> {
    const state = this.checkPermissionState();
    if (state === NotificationPermissionState.UNAVAILABLE) return;
    await this.requestPermissionAndInitialize();
  }

  /**
   * Fetch the current FCM token from Firebase.
   * Returns null if the token could not be retrieved.
   */
  private async fetchToken(): Promise<string | null> {
    if (!messaging || !this.serviceWorkerRegistration) return null;

    try {
      const token = await getToken(messaging, {
        vapidKey: VAPID_KEY,
        serviceWorkerRegistration: this.serviceWorkerRegistration,
      });

      if (!token) {
        logger.warn(TAG, 'FCM returned empty token');
        return null;
      }

      return token;
    } catch (error) {
      logger.error(TAG, 'Failed to get FCM token:', error);
      return null;
    }
  }

  /**
   * Start periodic token refresh. On each tick, fetches the current token
   * from Firebase and re-registers with the backend only if the token changed.
   */
  private startTokenRefresh(): void {
    this.stopTokenRefresh();
    this.tokenRefreshIntervalId = setInterval(async () => {
      await this.refreshToken();
    }, TOKEN_REFRESH_INTERVAL_MS);
  }

  /** Stop the periodic token refresh interval */
  private stopTokenRefresh(): void {
    if (this.tokenRefreshIntervalId) {
      clearInterval(this.tokenRefreshIntervalId);
      this.tokenRefreshIntervalId = null;
    }
  }

  /**
   * Check if the FCM token has been rotated and re-register with the backend if so.
   */
  private async refreshToken(): Promise<void> {
    const newToken = await this.fetchToken();
    if (!newToken) return;

    if (newToken === this.token) {
      logger.debug(TAG, 'Token unchanged, skipping re-registration');
      return;
    }

    const oldToken = this.token;
    this.setToken(newToken);
    logger.info(TAG, 'Token rotated, re-registering with backend');

    // Remove old token from backend before registering new one
    if (oldToken) {
      try {
        await removeDeviceByToken(oldToken);
      } catch (error) {
        logger.error(TAG, 'Failed to remove old token from backend:', error);
      }
    }

    try {
      await registerDevice({ token: newToken, timezone: getBrowserTimezone() });
    } catch (error) {
      logger.error(TAG, 'Failed to register new token on backend:', error);
    }
  }

  /**
   * Handle incoming FCM message and route to appropriate subscribers.
   *
   * Reads title/body from `data` first (data-only messages) with fallback to
   * `notification` field. Parses `silent` and `queryKeys` from string values.
   */
  private handleMessage(payload: FirebaseMessagePayload): void {
    logger.info(TAG, 'Received FCM message', payload);

    const data = payload.data;
    const notificationType = data?.type;
    const silent = data?.silent === 'true';
    const queryKeys =
      data?.queryKeys
        ?.split(',')
        .map(key => key.trim())
        .filter(Boolean) ?? [];

    const notification: NotificationPayload = {
      id: data?.id || Date.now().toString(),
      type: notificationType ?? 'unknown',
      silent,
      queryKeys,
      data: data ?? ({} as BaseFirebaseMessageData),
    };

    if (notificationType && this.listeners.has(notificationType as FcmNotificationType)) {
      const handlers = this.listeners.get(notificationType as FcmNotificationType);
      handlers?.forEach(handler => {
        handler(notification);
      });
    }

    if (this.listeners.has(FcmNotificationType.ALL)) {
      const handlers = this.listeners.get(FcmNotificationType.ALL);
      handlers?.forEach(handler => {
        handler(notification);
      });
    }
  }

  /**
   * Subscribe to notifications of a specific type
   *
   * @param notificationType - The notification type to subscribe to
   * @param handler - Callback function invoked when matching notification arrives
   * @returns Unsubscribe function that removes this specific handler when called
   */
  subscribe<TData extends Record<string, unknown>>(
    notificationType: FcmNotificationType,
    handler: NotificationHandler<TData>
  ): () => void {
    if (!this.listeners.has(notificationType)) {
      this.listeners.set(notificationType, []);
    }

    const handlers = this.listeners.get(notificationType);
    handlers?.push(handler);

    // Return unsubscribe function
    return () => {
      const handlers = this.listeners.get(notificationType);
      if (handlers) {
        const index = handlers.indexOf(handler);
        if (index > -1) {
          handlers.splice(index, 1);
        }
      }
    };
  }

  /**
   * Returns the current FCM device token.
   * Useful for debugging (e.g., sending test messages via Firebase Admin SDK).
   */
  getToken(): string | null {
    return this.token;
  }

  /**
   * Simulate an incoming FCM message for local testing.
   * Calls `handleMessage` directly, bypassing Firebase and the service worker.
   * Available only to debug tooling — not intended for production use.
   */
  simulateMessage(payload: FirebaseMessagePayload): void {
    this.handleMessage(payload);
  }

  /**
   * Unregister the current device from push notifications
   *
   * Should be called during logout to prevent notifications being sent
   * to a device where the user is no longer authenticated.
   */
  async unregisterDevice(): Promise<void> {
    if (!this.token) {
      logger.debug(TAG, 'No token to unregister');
      return;
    }

    try {
      await removeDeviceByToken(this.token);
    } catch (error) {
      logger.error(TAG, 'Failed to unregister device:', error);
    } finally {
      this.stopTokenRefresh();
      this.setToken(null);
      this.serviceWorkerRegistration = null;
      this.initialized = false;
    }
  }
}

export default new FirebaseNotificationService();
